import { EditNames, SegmentationControllerType } from "config/analysisConfig";
import { BBox3i, InitSegState, MaskColor, MeshReply, SegmentMask, SegState, StateApplier, StrokeSession, TileID, UndoDelta, Vec3, VolumeInfo, WorkTilePayload } from "./SegmentManagerTypes";
import { runWorkerTask } from "@features/analysis/rendering/Panels/workerPool";
import { applyManualLumenStrokeToCprLabelData, createOrGetTileActor, resetManualCprLabelSyncTargetsToBaseline, SurfaceContext, updateTileActor } from "@features/analysis/rendering/Panels/Renderers/vtkManagers";
import { scheduleRender } from "@features/analysis/rendering/Panels/renderState";
import { BitMaskMap, getManualVolumeFromSeriesId } from "@features/analysis/rendering/Panels/Renderers/CornerstoneManager";
import { configStore } from "config/configStore";

export const TILE_SIZE = 32;
export const TILE_HALO = 1;

type LabelAccessor = (i:number,j:number,k:number)=>number
type HandleAABB = (idx:number,i:number,j:number,k:number)=>void;
type VoxelIJKLike = { i: number; j: number; k: number };
type SegmentHistoryListener = (change: {
  delta: UndoDelta;
  invert: boolean;
}) => void;

export type ExternalVoxelEditParams = {
  sourceId: string;
  segmentLabel: SegmentationControllerType;
  added?: VoxelIJKLike[];
  removed?: VoxelIJKLike[];
};

let globalState : SegState | null = null;
const tileMap = new Map<number, {actor:any;mapper:any}>();
let editMeshSmoothingGeneration = 0;
const rcpTileSize = 1.0 / TILE_SIZE;
const segmentHistoryListeners = new Set<SegmentHistoryListener>();
export function getTileMap(){return tileMap};

function decomposeMask(labelValue: number): Uint32Array {
  const parts = new Uint32Array(32);
  let v = labelValue >>> 0;
  let count = 0;
  while (v !== 0) {
    const lowBit = v & -v;
    parts[count++] = lowBit;
    v ^= lowBit;
  }
  return parts.slice(0, count);
}

// 선형 인덱스 유틸
function linIndex(nx:number, ny:number, i:number,j:number,k:number) {
  return (k * ny + j) * nx + i;
}

function worldToIJK(origin:Vec3,spacing:Vec3,dir:number[], world:Vec3): Vec3 {
  const wx = world[0]-origin[0];
  const wy = world[1]-origin[1];
  const wz = world[2]-origin[2];

  const ix = ( dir[0]*wx + dir[3]*wy + dir[6]*wz ) / spacing[0];
  const iy = ( dir[1]*wx + dir[4]*wy + dir[7]*wz ) / spacing[1];
  const iz = ( dir[2]*wx + dir[5]*wy + dir[8]*wz ) / spacing[2];
  return [ix,iy,iz];
}

function ijkToWorld(origin:Vec3,spacing:Vec3,dir:number[], i:number, j:number, k:number): Vec3 {
  const x = origin[0] + (dir[0]*i + dir[1]*j + dir[2]*k) * spacing[0];
  const y = origin[1] + (dir[3]*i + dir[4]*j + dir[5]*k) * spacing[1];
  const z = origin[2] + (dir[6]*i + dir[7]*j + dir[8]*k) * spacing[2];
  return [x,y,z];
}


function dot(a:Vec3,b:Vec3){ return a[0]*b[0]+a[1]*b[1]+a[2]*b[2]; }
function sub(a:Vec3,b:Vec3):Vec3{ return [a[0]-b[0],a[1]-b[1],a[2]-b[2]]; }
function length(a:Vec3){ return Math.hypot(a[0],a[1],a[2]); }

function bitwiseKey(tx:number,ty:number,tz:number){return tx|(ty<<10)|(tz<<20);};
function bitwiseKeyInv(bitwiseKey:number){return {tx:bitwiseKey&0x3FF,ty:(bitwiseKey>>10)&0x3FF,tz:(bitwiseKey>>20)&0x3FF}}
function tileOf(i:number,j:number,k:number): TileID {
  return {
    tx: Math.floor((i + TILE_HALO)*rcpTileSize),
    ty: Math.floor((j + TILE_HALO)*rcpTileSize),
    tz: Math.floor((k + TILE_HALO)*rcpTileSize) };
}

function tilesFromBBoxWithHalo(b:BBox3i, nx:number, ny:number, nz:number): number[] {
  const i0 = Math.max(b.i0 - TILE_HALO, 0);
  const j0 = Math.max(b.j0 - TILE_HALO, 0);
  const k0 = Math.max(b.k0 - TILE_HALO, 0);
  const i1 = Math.min(b.i1 + TILE_HALO, nx-1);
  const j1 = Math.min(b.j1 + TILE_HALO, ny-1);
  const k1 = Math.min(b.k1 + TILE_HALO, nz-1);

  const t0 = tileOf(i0,j0,k0);
  const t1 = tileOf(i1,j1,k1);

  // console.log(i0,j0,k0,i1,j1,k1,t0,t1)
  const set = new Set<number>();
  const out: number[] = [];
  for (let tz=t0.tz; tz<=t1.tz; ++tz)
    for (let ty=t0.ty; ty<=t1.ty; ++ty)
      for (let tx=t0.tx; tx<=t1.tx; ++tx) {
        // const key = `${tx},${ty},${tz}`;
        const key = bitwiseKey(tx,ty,tz);
        if (!set.has(key)) { set.add(key); out.push(key); }
      }
  return out;
}

function comp(a:number[],b:number[]){
  return a.every((v,i)=>v===b[i])
}

function createSegState(params:InitSegState
): SegState {
  const {imageData,labelVolume,cachedStats,setCompleteScalarDataArray,setAtIndex,updateElements} = params
  const dim = imageData.getDimensions()
  const origin = imageData.getOrigin();     // [ox,oy,oz]
  const spacing = imageData.getSpacing();   // [sx,sy,sz]
  const direction = imageData.getDirection(); // row-major 3x3

  if(globalState &&
    comp(globalState.vol.dim,dim) &&
    comp(globalState.vol.origin,origin) &&
    comp(globalState.vol.spacing,spacing) &&
    comp(globalState.vol.direction,direction)
  ) {
    if (!globalState.externalDeltas) globalState.externalDeltas = new Map();
    if (!globalState.externalBaselines) {
      globalState.externalBaselines = new Map();
    }
    return globalState;
  }
  const vol :VolumeInfo= {dim,origin,spacing,direction}
  Array.from({length:4},()=>{
      runWorkerTask({type:'meshTilesInit',vol});
    })
  return {
    vol,
    masks: new Map(),
    cachedStats,
    labelVolume,
    labelIndexCache:new Map(),
    activeSeg: null,
    undoStack: [],
    redoStack: [],
    externalDeltas: new Map(),
    externalBaselines: new Map(),
    dirtyTiles: new Set(),
    setCompleteScalarDataArray,
    setAtIndex,
    updateElements,
  };
}

function tilesOwnedByIndices(
  indices: Uint32Array,
  NX:number, NY:number, NZ:number
): Set<number> {
  const NXY = NX*NY;
  const rcpNXY = 1.0/NXY;
  const rcpNX = 1.0 / NX;
  const keys = new Set<number>();
  const delta = [[0,-1],[0]]
  for (const idx of indices) {
    const k = Math.floor(idx * rcpNXY);
    const r = idx - k*NXY;
    const j = Math.floor(r * rcpNX);
    const i = r - j*NX;

    const dxs = (i % TILE_SIZE) === 0 ? delta[0] : delta[1];
    const dys = (j % TILE_SIZE) === 0 ? delta[0] : delta[1];
    const dzs = (k % TILE_SIZE) === 0 ? delta[0] : delta[1];

    for (const dx of dxs){
      for (const dy of dys){
        for (const dz of dzs){
          const ii = i + dx;
          const jj = j + dy;
          const kk = k + dz;
          if (ii<0 || jj<0 || kk<0) continue;

          const tx = Math.floor(ii * rcpTileSize);
          const ty = Math.floor(jj * rcpTileSize);
          const tz = Math.floor(kk * rcpTileSize);

          // keys.add(`${tx},${ty},${tz}`);
          keys.add(bitwiseKey(tx,ty,tz));
        }
      }
    }
  }
  return keys;
}
function concatUint32(chunks: Uint32Array[]): Uint32Array {
  let total=0; for(const c of chunks) total+=c.length;
  const out = new Uint32Array(total);
  let off=0; for(const c of chunks){ out.set(c, off); off+=c.length; }
  return out;
}

// function unionTileKeys(...sets: Set<string>[]): string[] {
function unionTileKeys(...sets: Set<number>[]): number[] {
  const out = new Set<number>();
  for (const s of sets) for (const k of s) out.add(k);
  return Array.from(out);
}

// 한번만
function getSparseIndexForLabel(
  state: SegState,
  segmentLabel: SegmentationControllerType
): Uint32Array {
  const cached = state.labelIndexCache?.get(segmentLabel);
  if (cached) return cached;

  const src = state.labelVolume;
  const indices: number[] = [];

  const hashIndices = state.cachedStats[segmentLabel] as Set<number>;

  const segmentIndices = new Set<number>(Object.entries(state.cachedStats.arrangeBitMask as Record<number,number>)
  .filter(([key,_])=>hashIndices.has(Number(key)))
  .map(([_,value])=>value));

  for (let i = 0; i < src.length; i+=4) {
    if (src[i]    && segmentIndices.has(src[i])) indices.push(i);
    if (src[i+1]  && segmentIndices.has(src[i+1])) indices.push(i+1);
    if (src[i+2]  && segmentIndices.has(src[i+2])) indices.push(i+2);
    if (src[i+3]  && segmentIndices.has(src[i+3])) indices.push(i+3);
  }

  const idx = new Uint32Array(indices);
  state.labelIndexCache?.set(segmentLabel, idx);
  return idx;
}

function materializeMaskFromLabel(
  state: SegState,
  segmentLabel: SegmentationControllerType
): SegmentMask {
  const sparse = getSparseIndexForLabel(state, segmentLabel);

  const nvox = state.vol.dim[0]*state.vol.dim[1]*state.vol.dim[2];
  const maskArr = new Uint8Array(nvox);

  sparse.forEach((idx)=>maskArr[idx]=1);
  // for (let n = 0; n < sparse.length; ++n) {
  //   maskArr[sparse[n]] = 1;
  // }
  const mask: SegmentMask = { data: maskArr,editsAdded:[],editsRemoved:[] };
  state.masks.set(segmentLabel, mask);
  return mask;
}

function clearRendererActors(renderer: any){
  if(!renderer) return;
  console.log('클리어 세그마네자')
  tileMap.forEach(({actor,mapper})=>{
    mapper.setInputData(null);
    renderer.removeActor(actor);
  })
  tileMap.clear();
}

export function restoreRendererActors(context: SurfaceContext) {
  if(tileMap.size === 0) return;
  const { renderer } = context;
  renderer.removeAllActors();
  tileMap.forEach(({ actor }) => {
    renderer.addActor(actor);
  });
  renderer.resetCamera();
}

export function updateRendererActorsColor(context: SurfaceContext, color: Vec3, visibility:boolean) {
  const currentTileColor = color.map((value) =>
    value > 1 ? value / 255 : value,
  ) as Vec3;

  tileMap.forEach(({ actor }) => {
    actor.setVisibility(visibility);
    actor.getProperty().setColor(...currentTileColor);
    actor.modified();
  });

  scheduleRender(context.rendererId, { type: 'updateRenderWindow' });
}

function refreshTiles(
  context: SurfaceContext,
  tileKeys: number[],
  maskBinary: Uint8Array,
  segmentLabel: SegmentationControllerType,
) {
  if (!globalState) throw 'ㅂㅂ';
  const { renderer, rendererId } = context;
  clearRendererActors(context.renderer);

  const dirtyTiles = new Set<number>(tileKeys);
  if (!dirtyTiles.size) return;

  updateTiles(
    {
      volInfo: globalState!.vol,
      volDim: globalState!.vol.dim,
      tileSize: TILE_SIZE,
      halo: TILE_HALO,
      strategy: 'HP',
      segmentLabel,
      maskBinary,
      renderer,
      requestRender() {
        //renderer.resetCamera();
        requestAnimationFrame(() => {
          scheduleRender(rendererId, { type: 'updateRenderWindow' });
        });
      },
    },
    Array.from(dirtyTiles),
  );

  globalState.dirtyTiles.clear();
}
export function ensureActiveMask(segmentLabel: SegmentationControllerType|null, context? : SurfaceContext) {
  if(!globalState) throw "이닛???"

  if(!segmentLabel){
    if(!context) return;
    globalState.activeSeg=segmentLabel;
    clearRendererActors(context.renderer);
    return;
  }

  const prevSeg = globalState.activeSeg;

  let mask = globalState.masks.get(segmentLabel);
  // if (found) return found.data;
  if(!mask){
    mask = materializeMaskFromLabel(globalState, segmentLabel);
  }

  if(prevSeg !== segmentLabel){
    const [NX,NY,NZ] = globalState.vol.dim;
    console.log(prevSeg,segmentLabel)
    if(!context) throw"여기는 라벨 바뀔때만 와야하는데?";

    console.time('update new segment label')
    globalState.setCompleteScalarDataArray(mask.data);
    globalState!.updateElements();


    const indicesCached = globalState.labelIndexCache?.get(segmentLabel);
    if(!indicesCached) throw "반드시 했을걸"
    const baseTiles = tilesOwnedByIndices(indicesCached,NX,NY,NZ);
    const addedTiles = !mask.editsAdded.length?new Set<number>():tilesOwnedByIndices(concatUint32(mask.editsAdded),NX,NY,NZ);
    const removedTiles = !mask.editsRemoved.length?new Set<number>():tilesOwnedByIndices(concatUint32(mask.editsRemoved),NX,NY,NZ);
    const activatedTiles = new Set(tileMap.keys());
    const tileKeys =  unionTileKeys(baseTiles,addedTiles,removedTiles,activatedTiles);
    // console.log(baseTiles,addedTiles,removedTiles,tileKeys,indicesCached)

    refreshTiles(context,tileKeys,mask.data,segmentLabel);
    // // 잡것들 지우고 타일맵 클리아
    // const {renderer,rendererId} = context;
    // clearRendererActors(context);

    // const dirtyTiles = new Set<number>(tileKeys);
    // if (!dirtyTiles.size) return mask;

    // updateTiles({
    //   volInfo: globalState!.vol,
    //   volDim: globalState!.vol.dim,
    //   tileSize: TILE_SIZE,
    //   halo: TILE_HALO,
    //   strategy:"HP",
    //   maskBinary: mask.data,
    //   renderer,
    //   requestRender() {
    //     scheduleRender(rendererId,{type:'updateRenderWindow'})
    //   },
    // }, Array.from(dirtyTiles));

    // globalState.dirtyTiles.clear();

    console.timeEnd('update new segment label')
    globalState.activeSeg = segmentLabel;
  }

  return mask;
}

export function filterActiveMaskLinearIndices(
  indices: Uint32Array,
  context?: SurfaceContext,
): Uint32Array {
  if (!globalState || !indices.length || !globalState.activeSeg) {
    return new Uint32Array();
  }

  const mask = ensureActiveMask(globalState.activeSeg, context);
  if (!mask) return new Uint32Array();

  const voxelCount =
    globalState.vol.dim[0] * globalState.vol.dim[1] * globalState.vol.dim[2];
  const filtered = new Uint32Array(indices.length);
  const seen = new Uint8Array(voxelCount);
  let filteredLength = 0;

  for (const idx of indices) {
    if (idx >= voxelCount || seen[idx]) continue;
    seen[idx] = 1;
    if ((mask.data[idx] & 0b1) === 1) {
      filtered[filteredLength] = idx;
      filteredLength += 1;
    }
  }

  return filtered.slice(0, filteredLength);
}

// function enterSegment(state: SegState, segmentLabel: SegmentationControllerType) {
//   state.activeSeg = segmentLabel;

//   if (!state.masks.has(segmentLabel)) {
//     materializeMaskFromLabel(state, segmentLabel);
//     // return state;
//   }

//   // return state;
// }


function computeSliceHalfThickness(
  spacing: Vec3,
  dir: number[],
  viewPlaneNormal: Vec3
): number {
  // console.time('computeSliceHalfThickness')
  const vx = [dir[0]*spacing[0], dir[3]*spacing[1], dir[6]*spacing[2]];
  const vy = [dir[1]*spacing[0], dir[4]*spacing[1], dir[7]*spacing[2]];
  const vz = [dir[2]*spacing[0], dir[5]*spacing[1], dir[8]*spacing[2]];

  const dotx = Math.abs(vx[0]*viewPlaneNormal[0] + vx[1]*viewPlaneNormal[1] + vx[2]*viewPlaneNormal[2]);
  const doty = Math.abs(vy[0]*viewPlaneNormal[0] + vy[1]*viewPlaneNormal[1] + vy[2]*viewPlaneNormal[2]);
  const dotz = Math.abs(vz[0]*viewPlaneNormal[0] + vz[1]*viewPlaneNormal[1] + vz[2]*viewPlaneNormal[2]);

  // console.timeEnd('computeSliceHalfThickness')
  return 0.5 * (dotx + doty + dotz);
}

function strokePointDistanceOnPlane(sess: StrokeSession, a: Vec3, b: Vec3) {
  const d = sub(b, a);
  const {uWorld, vWorld} = sess.plane;
  return Math.hypot(dot(uWorld, d), dot(vWorld, d));
}

function appendStrokePoint(sess: StrokeSession, pointWorld: Vec3) {
  if (!sess.pointsWorld) sess.pointsWorld = [];
  const last = sess.pointsWorld[sess.pointsWorld.length - 1];
  const minSpacing = Math.min(...sess.vol.spacing);
  const minStep = Math.max(minSpacing * 0.25, sess.radiusMM * 0.15, 0.1);

  if (!last || strokePointDistanceOnPlane(sess, last, pointWorld) >= minStep) {
    sess.pointsWorld.push(pointWorld);
  } else {
    sess.pointsWorld[sess.pointsWorld.length - 1] = pointWorld;
  }
}

type ClosedStrokeRegion = {
  u0: number;
  v0: number;
  gridStep: number;
  width: number;
  height: number;
  fillCells: Uint8Array;
};

function getClosedStrokeRegion(
  sess: StrokeSession,
  polygon: Float64Array,
): ClosedStrokeRegion | null {
  const points = sess.pointsWorld;
  if (!points || points.length < 4) return null;

  const minSpacing = Math.min(...sess.vol.spacing);
  const gridStep = Math.max(minSpacing * 0.5, 0.25);
  const boundaryRadius = Math.max(sess.radiusMM, gridStep);
  const margin = boundaryRadius + gridStep * 3;

  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  for (let i = 0; i < polygon.length; i += 2) {
    const u = polygon[i];
    const v = polygon[i + 1];
    if (u < minU) minU = u;
    if (u > maxU) maxU = u;
    if (v < minV) minV = v;
    if (v > maxV) maxV = v;
  }

  const u0 = Math.floor((minU - margin) / gridStep) * gridStep;
  const v0 = Math.floor((minV - margin) / gridStep) * gridStep;
  const width = Math.ceil((maxU + margin - u0) / gridStep) + 1;
  const height = Math.ceil((maxV + margin - v0) / gridStep) + 1;
  if (width <= 2 || height <= 2 || width * height > 16_000_000) return null;

  const cells = new Uint8Array(width * height);
  const radiusCells = Math.ceil(boundaryRadius / gridStep);
  const boundaryOffsets: number[] = [];
  const boundaryRadius2 = boundaryRadius * boundaryRadius;
  for (let y = -radiusCells; y <= radiusCells; y += 1) {
    for (let x = -radiusCells; x <= radiusCells; x += 1) {
      const du = x * gridStep;
      const dv = y * gridStep;
      if (du * du + dv * dv <= boundaryRadius2) {
        boundaryOffsets.push(x, y);
      }
    }
  }

  const markBoundary = (u: number, v: number) => {
    const cx = Math.round((u - u0) / gridStep);
    const cy = Math.round((v - v0) / gridStep);
    for (let n = 0; n < boundaryOffsets.length; n += 2) {
      const x = cx + boundaryOffsets[n];
      const y = cy + boundaryOffsets[n + 1];
      if (x < 0 || y < 0 || x >= width || y >= height) continue;
      cells[y * width + x] = 1;
    }
  };

  for (let i = 0; i < points.length - 1; i += 1) {
    const uA = polygon[i * 2];
    const vA = polygon[i * 2 + 1];
    const uB = polygon[(i + 1) * 2];
    const vB = polygon[(i + 1) * 2 + 1];
    const du = uB - uA;
    const dv = vB - vA;
    const steps = Math.max(1, Math.ceil(Math.hypot(du, dv) / (gridStep * 0.5)));
    for (let t = 0; t <= steps; t += 1) {
      const ratio = t / steps;
      markBoundary(uA + du * ratio, vA + dv * ratio);
    }
  }

  const queue = new Int32Array(width * height);
  let head = 0;
  let tail = 0;
  const pushOutside = (x: number, y: number) => {
    const idx = y * width + x;
    if (cells[idx] !== 0) return;
    cells[idx] = 2;
    queue[tail] = idx;
    tail += 1;
  };

  for (let x = 0; x < width; x += 1) {
    pushOutside(x, 0);
    pushOutside(x, height - 1);
  }
  for (let y = 1; y < height - 1; y += 1) {
    pushOutside(0, y);
    pushOutside(width - 1, y);
  }

  while (head < tail) {
    const idx = queue[head];
    head += 1;
    const x = idx % width;
    const y = Math.floor(idx / width);
    if (x > 0) pushOutside(x - 1, y);
    if (x < width - 1) pushOutside(x + 1, y);
    if (y > 0) pushOutside(x, y - 1);
    if (y < height - 1) pushOutside(x, y + 1);
  }

  const fillCells = new Uint8Array(width * height);
  let hasInterior = false;
  for (let i = 0; i < cells.length; i += 1) {
    if (cells[i] !== 0) continue;
    hasInterior = true;
    fillCells[i] = 1;
  }

  if (!hasInterior) return null;

  const buildBoundaryDistance = (sourceCell: number) => {
    const dist = new Int16Array(width * height);
    dist.fill(-1);
    let qHead = 0;
    let qTail = 0;

    const seedBoundary = (idx: number, distance = 1) => {
      if (cells[idx] !== 1 || dist[idx] >= 0) return;
      dist[idx] = distance;
      queue[qTail] = idx;
      qTail += 1;
    };

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const idx = y * width + x;
        if (cells[idx] !== sourceCell) continue;
        if (x > 0) seedBoundary(idx - 1);
        if (x < width - 1) seedBoundary(idx + 1);
        if (y > 0) seedBoundary(idx - width);
        if (y < height - 1) seedBoundary(idx + width);
      }
    }

    while (qHead < qTail) {
      const idx = queue[qHead];
      qHead += 1;
      const distance = dist[idx] + 1;
      const x = idx % width;
      const y = Math.floor(idx / width);

      if (x > 0) seedBoundary(idx - 1, distance);
      if (x < width - 1) seedBoundary(idx + 1, distance);
      if (y > 0) seedBoundary(idx - width, distance);
      if (y < height - 1) seedBoundary(idx + width, distance);
    }

    return dist;
  };

  const interiorDistance = buildBoundaryDistance(0);
  const outsideDistance = buildBoundaryDistance(2);
  for (let i = 0; i < cells.length; i += 1) {
    if (cells[i] !== 1 || interiorDistance[i] < 0) continue;
    if (
      outsideDistance[i] < 0 ||
      interiorDistance[i] <= outsideDistance[i] + 1
    ) {
      fillCells[i] = 1;
    }
  }

  return {u0, v0, gridStep, width, height, fillCells};
}

function fillClosedStrokeInterior(sess: StrokeSession) {
  if (!globalState || !globalState.activeSeg) return;

  const points = sess.pointsWorld;
  if (!points || points.length < 4) return;

  const segmentMask = ensureActiveMask(globalState.activeSeg);
  if (!segmentMask) return;

  const {data, editsAdded, editsRemoved} = segmentMask;
  const {origin, spacing, direction} = sess.vol;
  const [nx, ny, nz] = globalState.vol.dim;
  const h = computeSliceHalfThickness(spacing, direction, sess.plane.normalWorld);
  const radVox = Math.max(...spacing.map((s) => sess.radiusMM / s)) + 3;
  const originPoint = points[0];
  const polygon = new Float64Array(points.length * 2);
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  let area = 0;

  for (let n = 0; n < points.length; n += 1) {
    const d = sub(points[n], originPoint);
    const u = dot(sess.plane.uWorld, d);
    const v = dot(sess.plane.vWorld, d);
    polygon[n * 2] = u;
    polygon[n * 2 + 1] = v;
    if (u < minU) minU = u;
    if (u > maxU) maxU = u;
    if (v < minV) minV = v;
    if (v > maxV) maxV = v;
  }

  for (let n = 0, prev = points.length - 1; n < points.length; prev = n, n += 1) {
    area +=
      polygon[prev * 2] * polygon[n * 2 + 1] -
      polygon[n * 2] * polygon[prev * 2 + 1];
  }
  if (Math.abs(area) < Math.min(...spacing) * Math.min(...spacing) * 2) return;
  const closedRegion = getClosedStrokeRegion(sess, polygon);
  if (!closedRegion) return;
  const {u0, v0, gridStep, width, height, fillCells} = closedRegion;

  let i0 = nx - 1;
  let j0 = ny - 1;
  let k0 = nz - 1;
  let i1 = 0;
  let j1 = 0;
  let k1 = 0;

  for (const point of sess.pointsWorld!) {
    const ijk = worldToIJK(origin, spacing, direction, point);
    i0 = Math.min(i0, Math.floor(ijk[0] - radVox));
    j0 = Math.min(j0, Math.floor(ijk[1] - radVox));
    k0 = Math.min(k0, Math.floor(ijk[2] - radVox));
    i1 = Math.max(i1, Math.ceil(ijk[0] + radVox));
    j1 = Math.max(j1, Math.ceil(ijk[1] + radVox));
    k1 = Math.max(k1, Math.ceil(ijk[2] + radVox));
  }

  i0 = Math.max(0, i0);
  j0 = Math.max(0, j0);
  k0 = Math.max(0, k0);
  i1 = Math.min(nx - 1, i1);
  j1 = Math.min(ny - 1, j1);
  k1 = Math.min(nz - 1, k1);

  const iAxis: Vec3 = [
    direction[0] * spacing[0],
    direction[3] * spacing[1],
    direction[6] * spacing[2],
  ];
  const jAxis: Vec3 = [
    direction[1] * spacing[0],
    direction[4] * spacing[1],
    direction[7] * spacing[2],
  ];
  const kAxis: Vec3 = [
    direction[2] * spacing[0],
    direction[5] * spacing[1],
    direction[8] * spacing[2],
  ];
  const uStepI = dot(sess.plane.uWorld, iAxis);
  const uStepJ = dot(sess.plane.uWorld, jAxis);
  const uStepK = dot(sess.plane.uWorld, kAxis);
  const vStepI = dot(sess.plane.vWorld, iAxis);
  const vStepJ = dot(sess.plane.vWorld, jAxis);
  const vStepK = dot(sess.plane.vWorld, kAxis);
  const nStepI = dot(sess.plane.normalWorld, iAxis);
  const nStepJ = dot(sess.plane.normalWorld, jAxis);
  const nStepK = dot(sess.plane.normalWorld, kAxis);
  const baseWorld = ijkToWorld(origin, spacing, direction, i0, j0, k0);
  const baseD = sub(baseWorld, originPoint);
  const baseU = dot(sess.plane.uWorld, baseD);
  const baseV = dot(sess.plane.vWorld, baseD);
  const baseN = dot(sess.plane.normalWorld, baseD);

  const edits: number[] = [];
  for (let k = k0; k <= k1; ++k) {
    const dk = k - k0;
    const kU = baseU + dk * uStepK;
    const kV = baseV + dk * vStepK;
    const kN = baseN + dk * nStepK;

    for (let j = j0; j <= j1; ++j) {
      const dj = j - j0;
      let u = kU + dj * uStepJ;
      let v = kV + dj * vStepJ;
      let nDist = kN + dj * nStepJ;
      let idx = linIndex(nx, ny, i0, j, k);

      for (let i = i0; i <= i1; ++i) {
        if (
          data[idx] === sess.segmentIndex ||
          Math.abs(nDist) > h ||
          u < minU || u > maxU ||
          v < minV || v > maxV
        ) {
          idx += 1;
          u += uStepI;
          v += vStepI;
          nDist += nStepI;
          continue;
        }

        const gridX = Math.round((u - u0) / gridStep);
        const gridY = Math.round((v - v0) / gridStep);
        if (
          gridX < 0 || gridY < 0 ||
          gridX >= width || gridY >= height ||
          fillCells[gridY * width + gridX] !== 1
        ) {
          idx += 1;
          u += uStepI;
          v += vStepI;
          nDist += nStepI;
          continue;
        }

        data[idx] = sess.segmentIndex;
        globalState.setAtIndex(idx, sess.segmentIndex);
        sess.flippedThisStroke.push(idx);
        edits.push(idx);

        if (i < sess.bbox.i0) sess.bbox.i0 = i;
        if (i > sess.bbox.i1) sess.bbox.i1 = i;
        if (j < sess.bbox.j0) sess.bbox.j0 = j;
        if (j > sess.bbox.j1) sess.bbox.j1 = j;
        if (k < sess.bbox.k0) sess.bbox.k0 = k;
        if (k > sess.bbox.k1) sess.bbox.k1 = k;

        idx += 1;
        u += uStepI;
        v += vStepI;
        nDist += nStepI;
      }
    }
  }

  if (edits.length) {
    (sess.segmentIndex === 1 ? editsAdded : editsRemoved).push(
      new Uint32Array(edits),
    );
  }
}


// function approximateStrokeAABB({
//   state,segmentLabel,stroke, centers, handleAABB
// }:{
//   state: SegState,
//   segmentLabel: SegmentationControllerType,
//   stroke:DragStroke|StrokeSession,
//   centers:Vec3[],
//   handleAABB:HandleAABB
// }){
//   const nx = state.vol.dim[0], ny=state.vol.dim[1], nz=state.vol.dim[2];
//   const {data} = ensureActiveMask(segmentLabel)

//   const {origin,spacing,direction} = stroke.vol;
//   const h= computeSliceHalfThickness(spacing,direction,stroke.plane.normalWorld);
//   const r = stroke.radiusMM
//   const r2 = r*r;
//   const radVox = r/ spacing[0];

//   const n = stroke.plane.normalWorld;
//   const uAx = stroke.plane.uWorld;
//   const vAx = stroke.plane.vWorld;

//   for(const center of centers){
//     const cIJK = worldToIJK(origin,spacing,direction, center);

//     const i0 = Math.max(0, Math.floor(cIJK[0] - radVox - 1));
//     const i1 = Math.min(nx-1, Math.ceil (cIJK[0] + radVox + 1));
//     const j0 = Math.max(0, Math.floor(cIJK[1] - radVox - 1));
//     const j1 = Math.min(ny-1, Math.ceil (cIJK[1] + radVox + 1));
//     const k0 = Math.max(0, Math.floor(cIJK[2] - radVox - 1));
//     const k1 = Math.min(nz-1, Math.ceil (cIJK[2] + radVox + 1));

//     for (let k=k0; k<=k1; ++k) {
//       for (let j=j0; j<=j1; ++j) {
//         for (let i=i0; i<=i1; ++i) {
//             const idx = linIndex(nx,ny,i,j,k);
//             if (data[idx] === 1) continue;

//             const pW = ijkToWorld(origin,spacing,direction, i, j, k);
//             const pc = sub(pW, center);
//             const distPlane = Math.abs(dot(n, pc));
//             if (distPlane > h) continue;

//             const u = dot(uAx, pc);
//             const v = dot(vAx, pc);
//             if ((u*u + v*v) > r2) continue;

//             data[idx] = 1;

//             handleAABB(idx,i,j,k);
//         }
//       }
//     }
//   }
// }

function hasSortedLinearIndex(
  indices: Uint32Array | null | undefined,
  target: number,
) {
  if (!indices?.length) return false;

  let low = 0;
  let high = indices.length - 1;
  while (low <= high) {
    const middle = (low + high) >>> 1;
    const value = indices[middle];
    if (value === target) return true;
    if (value < target) {
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return false;
}

function hasSegmentMaskValue(value: number, segmentIndex: number) {
  if (segmentIndex === 0 || segmentIndex === 1) {
    return (value & 0b1) === segmentIndex;
  }

  return value === segmentIndex;
}

function paintAtPointStreamingCore(
  sess: StrokeSession,
  cntx : SurfaceContext,
  pointWorld: Vec3,
  allowedIndices?: Uint32Array | null,
) {
  if(!globalState) return;
  // const {activeSeg} = globalState
  // if(!activeSeg) return;

  const nx = globalState.vol.dim[0], ny=globalState.vol.dim[1], nz=globalState.vol.dim[2];
  const {radiusMM:r, vol, plane, lastWorld, segmentIndex } = sess;
  const {origin,spacing,direction} = vol;
  const {normalWorld: n, uWorld: uAx, vWorld: vAx} = plane;
  const r2 = r*r;
  const radVox = r/ spacing[0];
  const constrainedToAllowedIndices = allowedIndices !== undefined;

  {
    //채워지는가?
    appendStrokePoint(sess, pointWorld);
  }

  const centers: Vec3[] = [];
  const edits  = [];

  if (!lastWorld) {
    centers.push(pointWorld);
  } else {
    const a = lastWorld, b = pointWorld;
    const dx=b[0]-a[0], dy=b[1]-a[1], dz=b[2]-a[2];
    const len = Math.hypot(dx,dy,dz);
    const stepMM = Math.max(1, r*0.5); // 반경의 절반 간격
    const nSteps = Math.max(1, Math.floor(len/stepMM));
    const rcpN = 1.0/nSteps;
    for (let t=0; t<=nSteps; ++t) {
      centers.push([a[0]+dx*t*rcpN, a[1]+dy*t*rcpN, a[2]+dz*t*rcpN]);
    }
  }
  sess.lastWorld = pointWorld;

  if (constrainedToAllowedIndices && !allowedIndices?.length) return 0;

  const h= computeSliceHalfThickness(spacing,direction,n);

  const segmentMask = ensureActiveMask(globalState.activeSeg!)
  if(!segmentMask) return;
  const {data,editsAdded,editsRemoved} = segmentMask;

  for(const center of centers){
    const cIJK = worldToIJK(origin,spacing,direction, center);

    const i0 = Math.max(0, Math.floor(cIJK[0] - radVox - 1));
    const i1 = Math.min(nx-1, Math.ceil (cIJK[0] + radVox + 1));
    const j0 = Math.max(0, Math.floor(cIJK[1] - radVox - 1));
    const j1 = Math.min(ny-1, Math.ceil (cIJK[1] + radVox + 1));
    const k0 = Math.max(0, Math.floor(cIJK[2] - radVox - 1));
    const k1 = Math.min(nz-1, Math.ceil (cIJK[2] + radVox + 1));

    for (let k=k0; k<=k1; ++k) {
      for (let j=j0; j<=j1; ++j) {
        for (let i=i0; i<=i1; ++i) {
          const idx = linIndex(nx,ny,i,j,k);
          if (
            constrainedToAllowedIndices &&
            !hasSortedLinearIndex(allowedIndices, idx)
          ) {
            continue;
          }
          if (
            constrainedToAllowedIndices
              ? hasSegmentMaskValue(data[idx], segmentIndex)
              : data[idx] === segmentIndex
          ) continue;

          const pW = ijkToWorld(origin,spacing,direction, i, j, k);
          const pc = sub(pW, center);
          const distPlane = Math.abs(dot(n, pc));
          if (distPlane > h) continue;

          const u = dot(uAx, pc);
          const v = dot(vAx, pc);
          if ((u*u + v*v) > r2) continue;

          data[idx] = segmentIndex;

          sess.flippedThisStroke.push(idx);
          if (i<sess.bbox.i0) sess.bbox.i0=i; if (i>sess.bbox.i1) sess.bbox.i1=i;
          if (j<sess.bbox.j0) sess.bbox.j0=j; if (j>sess.bbox.j1) sess.bbox.j1=j;
          if (k<sess.bbox.k0) sess.bbox.k0=k; if (k>sess.bbox.k1) sess.bbox.k1=k;

          globalState.setAtIndex(idx, segmentIndex);
          edits.push(idx);
        }
      }
    }
  }

  if(!!edits.length){
    (segmentIndex===1?editsAdded:editsRemoved).push(new Uint32Array(edits));
  }

  if(sess.bbox.i1 >= sess.bbox.i0){
    const [nx,ny,nz] =globalState.vol.dim;
    const tiles = tilesFromBBoxWithHalo(sess.bbox,nx,ny,nz);
    // console.log(tiles)
    for(const t of tiles){
      // if(!sess.dirtyKeys.has(t)){
        sess.dirtyKeys.add(t);
        globalState.dirtyTiles.add(t);
      // }
    }
  }

  return edits.length;
}

export function paintAtPointStreaming(
  sess: StrokeSession,
  cntx : SurfaceContext,
  pointWorld: Vec3
) {
  paintAtPointStreamingCore(sess, cntx, pointWorld);
}

export function paintAtPointStreamingWithinIndices(
  sess: StrokeSession,
  cntx : SurfaceContext,
  pointWorld: Vec3,
  allowedIndices: Uint32Array | null | undefined,
) {
  return paintAtPointStreamingCore(
    sess,
    cntx,
    pointWorld,
    allowedIndices ?? null,
  ) ?? 0;
}

function syncLumenStrokeDeltaToCprLabelData(delta: UndoDelta, invert = false) {
  if (!globalState) return;
  if (configStore.get().EditMode !== EditNames.lumen) return;
  const targetValue = invert ? Number(!delta.segmentIndex) : delta.segmentIndex;
  if (targetValue === 1) {
    applyManualLumenStrokeToCprLabelData({
      addedIndices: delta.flippedIndices,
    });
  } else {
    applyManualLumenStrokeToCprLabelData({
      removedIndices: delta.flippedIndices,
    });
  }
}

function notifySegmentHistoryChanged(delta: UndoDelta, invert = false) {
  segmentHistoryListeners.forEach((listener) => listener({ delta, invert }));
}

export function endStrokeState(sess: StrokeSession, cntx : SurfaceContext){
  if(!globalState) return;

  {
    ////채워지는가?
    if (!sess.disableClosedFill) {
      fillClosedStrokeInterior(sess);
    }

    if (sess.bbox.i1 >= sess.bbox.i0) {
      const [nx, ny, nz] = globalState.vol.dim;
      const tiles = tilesFromBBoxWithHalo(sess.bbox, nx, ny, nz);
      for (const t of tiles) {
        sess.dirtyKeys.add(t);
        globalState.dirtyTiles.add(t);
      }
    }
  }

  if(sess.flippedThisStroke.length){
    const delta : UndoDelta= {
      segmentLabel: globalState.activeSeg!,
      flippedIndices: Uint32Array.from(sess.flippedThisStroke),
      segmentIndex:sess.segmentIndex
    };
    globalState.redoStack = [];
    globalState.undoStack.push(delta);
    syncLumenStrokeDeltaToCprLabelData(delta);
    notifySegmentHistoryChanged(delta);
    updateState();
  }

  sess.dirtyKeys.forEach(k=>globalState?.dirtyTiles.add(k))
  requestRenderUpdate(cntx);
  // console.log(sess,globalState)
}
// export function applyBrushStrokeFill(globalState: SegState, stroke: DragStroke) {
//   if (globalState.activeSeg == null || globalState.activeSeg !== stroke.segmentLabel) return;
//   const nx = globalState.vol.dim[0], ny=globalState.vol.dim[1], nz=globalState.vol.dim[2];

//   let bbox: BBox3i = { i0:nx-1, j0:ny-1, k0:nz-1, i1:0, j1:0, k1:0 };
//   const flipped: number[] = [];

//   const handleAABB : HandleAABB = (idx:number,i:number,j:number,k:number)=>{
//     flipped.push(idx);
//     if (i<bbox.i0) bbox.i0=i; if (i>bbox.i1) bbox.i1=i;
//     if (j<bbox.j0) bbox.j0=j; if (j>bbox.j1) bbox.j1=j;
//     if (k<bbox.k0) bbox.k0=k; if (k>bbox.k1) bbox.k1=k;
//   }
//   approximateStrokeAABB({
//     centers:stroke.pointsWorld,
//     handleAABB,
//     segmentLabel:stroke.segmentLabel,
//     state:globalState,
//     stroke
//   })

//   if (flipped.length === 0) return;

//   const tiles = tilesFromBBoxWithHalo(bbox, nx, ny, nz);
//   // const keys: string[] = [];
//   for (const t of tiles) {
//     globalState.dirtyTiles.add(t);
//     // keys.push(t);
//   }

//   const delta : UndoDelta = {
//     segmentLabel: stroke.segmentLabel,
//     flippedIndices: Uint32Array.from(flipped),
//     segmentIndex:1
//   };

//   globalState.redoStack = [];
//   globalState.undoStack.push(delta);

//   // request3DUpdate(cntx);
//   // return { state, undoDelta, dirtyTiles: keys };
// }

function markDirtyTilesFromIndices(state:SegState, linIdx: Uint32Array) {
  const [NX, NY, NZ] = state.vol.dim;
  const NXY =(NX*NY);
  for (let n=0; n<linIdx.length; ++n) {
    const idx = linIdx[n];
    const k = Math.floor(idx / NXY);
    const r = idx - k*NXY;
    const j = Math.floor(r / NX);
    const i = r - j*NX;

    for (let dz = -1; dz <= 0; ++dz)
      for (let dy = -1; dy <= 0; ++dy)
        for (let dx = -1; dx <= 0; ++dx) {
          const ii = i + dx, jj = j + dy, kk = k + dz;
          if (ii < 0 || jj < 0 || kk < 0) continue;
          const tx = Math.floor(ii * rcpTileSize);
          const ty = Math.floor(jj * rcpTileSize);
          const tz = Math.floor(kk * rcpTileSize);
          // state.dirtyTiles.add(`${tx},${ty},${tz}`);
          state.dirtyTiles.add(bitwiseKey(tx,ty,tz));
        }
    // state.dirtyTiles.add(`${tx},${ty},${tz}`);
  }
}


function fillIndices(indices:Uint32Array, segmentIndex: number,segmentLabel?:SegmentationControllerType){
  if(!globalState) return;

  // const {segmentLabel, indices, segmentIndex} = params;

  const seg = globalState.masks.get(segmentLabel??globalState.activeSeg!);
  if (!seg) return;
  // console.log(segmentIndex,segmentLabel)
  const arr = seg.data;
  for (let n=0; n<indices.length; ++n) {
    arr[indices[n]] = segmentIndex;
    globalState.setAtIndex(indices[n],segmentIndex);
  }
  markDirtyTilesFromIndices(globalState, indices);

  // globalState.updateElements();
}

function voxelIJKToLinearIndices(
  voxels: VoxelIJKLike[] | undefined,
): Uint32Array {
  if (!globalState || !voxels?.length) return new Uint32Array();

  const [nx, ny, nz] = globalState.vol.dim;
  const seen = new Set<number>();
  const indices: number[] = [];

  for (const voxel of voxels) {
    const i = Math.round(voxel.i);
    const j = Math.round(voxel.j);
    const k = Math.round(voxel.k);
    if (i < 0 || j < 0 || k < 0) continue;
    if (i >= nx || j >= ny || k >= nz) continue;

    const idx = linIndex(nx, ny, i, j, k);
    if (seen.has(idx)) continue;
    seen.add(idx);
    indices.push(idx);
  }

  return new Uint32Array(indices);
}

function applyExternalDelta(delta: UndoDelta, invert = false) {
  fillIndices(
    delta.flippedIndices,
    invert ? Number(!delta.segmentIndex) : delta.segmentIndex,
    delta.segmentLabel,
  );
}

function buildDeltaForTargetValue(
  indices: Uint32Array,
  segmentLabel: SegmentationControllerType,
  segmentIndex: number,
): UndoDelta | null {
  if (!globalState || !indices.length) return null;

  const segmentMask = globalState.masks.get(segmentLabel);
  if (!segmentMask) return null;

  const filtered: number[] = [];
  for (const idx of indices) {
    if (segmentMask.data[idx] === segmentIndex) continue;
    filtered.push(idx);
  }

  if (!filtered.length) return null;

  return {
    segmentLabel,
    segmentIndex,
    flippedIndices: Uint32Array.from(filtered),
  };
}

function buildBaselineDeltas(
  indices: Uint32Array,
  segmentLabel: SegmentationControllerType,
): UndoDelta[] {
  if (!globalState || !indices.length) return [];

  const segmentMask = globalState.masks.get(segmentLabel);
  if (!segmentMask) return [];

  const grouped: Record<0 | 1, number[]> = { 0: [], 1: [] };
  const seen = new Set<number>();

  for (const idx of indices) {
    if (seen.has(idx)) continue;
    seen.add(idx);
    grouped[(segmentMask.data[idx] ? 1 : 0) as 0 | 1].push(idx);
  }

  return ([0, 1] as const)
    .filter((segmentIndex) => grouped[segmentIndex].length > 0)
    .map((segmentIndex) => ({
      segmentLabel,
      segmentIndex,
      flippedIndices: Uint32Array.from(grouped[segmentIndex]),
    }));
}

function mergeLinearIndices(...groups: Uint32Array[]) {
  const seen = new Set<number>();
  const merged: number[] = [];

  for (const group of groups) {
    for (const idx of group) {
      if (seen.has(idx)) continue;
      seen.add(idx);
      merged.push(idx);
    }
  }

  return Uint32Array.from(merged);
}

function excludeLinearIndices(indices: Uint32Array, excludes: Uint32Array) {
  if (!indices.length || !excludes.length) return indices;

  const excludeSet = new Set<number>(excludes);
  const filtered: number[] = [];
  for (const idx of indices) {
    if (!excludeSet.has(idx)) filtered.push(idx);
  }

  return Uint32Array.from(filtered);
}

function flattenExternalDeltas(
  deltaMap: SegState['externalDeltas'] | SegState['externalBaselines'],
) {
  return Array.from(deltaMap.values()).flat();
}

function getRollbackTrackedDeltas(state: SegState) {
  return [
    ...state.undoStack,
    ...flattenExternalDeltas(state.externalDeltas),
    ...flattenExternalDeltas(state.externalBaselines),
  ];
}


export type SerializedUndoDelta = {
  segmentLabel: SegmentationControllerType;
  segmentIndex: number;
  flippedIndices: Uint32Array;
};

export type SerializedSegmentManagerDraft = {
  version: 1;
  undoStack: SerializedUndoDelta[];
  redoStack: SerializedUndoDelta[];
  externalDeltas: Array<[string, SerializedUndoDelta[]]>;
  externalBaselines: Array<[string, SerializedUndoDelta[]]>;
  activeSeg: SegmentationControllerType | null;
};

function cloneDelta(delta: UndoDelta): SerializedUndoDelta {
  return {
    segmentLabel: delta.segmentLabel,
    segmentIndex: delta.segmentIndex,
    flippedIndices: Uint32Array.from(delta.flippedIndices),
  };
}

function cloneDeltaList(deltas: UndoDelta[]) {
  return deltas.map(cloneDelta);
}

function cloneDeltaMap(deltaMap: SegState['externalDeltas']) {
  return Array.from(deltaMap.entries()).map(
    ([sourceId, deltas]) => [sourceId, cloneDeltaList(deltas)] as [string, SerializedUndoDelta[]],
  );
}

function restoreDelta(delta: SerializedUndoDelta): UndoDelta {
  return {
    segmentLabel: delta.segmentLabel,
    segmentIndex: delta.segmentIndex,
    flippedIndices: Uint32Array.from(delta.flippedIndices),
  };
}

function restoreDeltaList(deltas: SerializedUndoDelta[]) {
  return deltas.map(restoreDelta);
}

function restoreDeltaMap(entries: Array<[string, SerializedUndoDelta[]]>) {
  return new Map(entries.map(([sourceId, deltas]) => [sourceId, restoreDeltaList(deltas)]));
}

export function createSegmentManagerDraftSnapshot(): SerializedSegmentManagerDraft | null {
  if (!globalState) return null;

  const externalDeltaEntries = cloneDeltaMap(globalState.externalDeltas);
  const hasChanges =
    globalState.undoStack.length > 0 ||
    globalState.redoStack.length > 0 ||
    externalDeltaEntries.some(([, deltas]) => deltas.length > 0);

  if (!hasChanges) return null;

  return {
    version: 1,
    undoStack: cloneDeltaList(globalState.undoStack),
    redoStack: cloneDeltaList(globalState.redoStack),
    externalDeltas: externalDeltaEntries,
    externalBaselines: cloneDeltaMap(globalState.externalBaselines),
    activeSeg: globalState.activeSeg,
  };
}

export function restoreSegmentManagerDraftSnapshot(
  draft: SerializedSegmentManagerDraft,
  cntx: SurfaceContext,
) {
  if (!globalState || !draft || draft.version !== 1) return false;

  resetSegmentMasks(cntx);

  const undoStack = restoreDeltaList(draft.undoStack);
  const redoStack = restoreDeltaList(draft.redoStack);
  const externalDeltas = restoreDeltaMap(draft.externalDeltas);
  const externalBaselines = restoreDeltaMap(draft.externalBaselines);

  const visibleDeltas = [
    ...undoStack,
    ...Array.from(externalDeltas.values()).flat(),
  ];

  visibleDeltas.forEach((delta) => {
    ensureActiveMask(delta.segmentLabel, cntx);
    applyExternalDelta(delta);
    syncLumenStrokeDeltaToCprLabelData(delta);
    markDirtyTilesFromIndices(globalState!, delta.flippedIndices);
  });

  globalState.undoStack = undoStack;
  globalState.redoStack = redoStack;
  globalState.externalDeltas = externalDeltas;
  globalState.externalBaselines = externalBaselines;
  globalState.activeSeg = draft.activeSeg;

  if (globalState.activeSeg) {
    ensureActiveMask(globalState.activeSeg, cntx);
    const activeMask = globalState.masks.get(globalState.activeSeg);
    if (activeMask) {
      globalState.setCompleteScalarDataArray(activeMask.data);
      globalState.updateElements();
    }
  }

  updateState();
  requestRenderUpdate(cntx);
  return true;
}

export function applyExternalVoxelEditState(
  params: ExternalVoxelEditParams,
  cntx: SurfaceContext,
) {
  if (!globalState) return null;

  ensureActiveMask(params.segmentLabel, cntx);

  const previousBaselines =
    globalState.externalBaselines.get(params.sourceId) ?? [];
  for (const baseline of previousBaselines) {
    applyExternalDelta(baseline);
  }

  const addedIndices = voxelIJKToLinearIndices(params.added);
  const removedIndices = excludeLinearIndices(
    voxelIJKToLinearIndices(params.removed),
    addedIndices,
  );
  const removeDelta = buildDeltaForTargetValue(
    removedIndices,
    params.segmentLabel,
    0,
  );
  const addDelta = buildDeltaForTargetValue(
    addedIndices,
    params.segmentLabel,
    1,
  );
  const changedIndices = mergeLinearIndices(
    removeDelta?.flippedIndices ?? new Uint32Array(),
    addDelta?.flippedIndices ?? new Uint32Array(),
  );
  const baselineDeltas = buildBaselineDeltas(
    changedIndices,
    params.segmentLabel,
  );

  if (removeDelta) applyExternalDelta(removeDelta);
  if (addDelta) applyExternalDelta(addDelta);

  const nextDeltas: UndoDelta[] = [];
  const segmentMask = globalState.masks.get(params.segmentLabel);

  if (removeDelta) {
    segmentMask?.editsRemoved.push(removeDelta.flippedIndices);
    nextDeltas.push(removeDelta);
  }

  if (addDelta) {
    segmentMask?.editsAdded.push(addDelta.flippedIndices);
    nextDeltas.push(addDelta);
  }

  if (nextDeltas.length) {
    globalState.externalDeltas.set(params.sourceId, nextDeltas);
    globalState.externalBaselines.set(params.sourceId, baselineDeltas);
  } else {
    globalState.externalDeltas.delete(params.sourceId);
    globalState.externalBaselines.delete(params.sourceId);
  }

  requestRenderUpdate(cntx);
  updateState();

  return {
    addedVoxelCount: addDelta?.flippedIndices.length ?? 0,
    removedVoxelCount: removeDelta?.flippedIndices.length ?? 0,
  };
}

export function commitExternalVoxelEditHistory(sourceId: string) {
  if (!globalState || !sourceId) return null;

  const deltas = globalState.externalDeltas.get(sourceId) ?? [];
  const committedDeltas = deltas
    .filter((delta) => delta.flippedIndices.length > 0)
    .map((delta) => ({
      ...delta,
      flippedIndices: Uint32Array.from(delta.flippedIndices),
    }));

  globalState.externalDeltas.delete(sourceId);
  globalState.externalBaselines.delete(sourceId);

  if (!committedDeltas.length) {
    updateState();
    return null;
  }

  globalState.redoStack = [];
  committedDeltas.forEach((delta) => {
    globalState?.undoStack.push(delta);
    notifySegmentHistoryChanged(delta);
  });
  updateState();

  return {
    deltaCount: committedDeltas.length,
    voxelCount: committedDeltas.reduce(
      (total, delta) => total + delta.flippedIndices.length,
      0,
    ),
  };
}
export function resetSegmentMasks(cntx:SurfaceContext){
  if(!globalState) return;
  //undo들에서 건든놈 danawa
  const rollbackDeltas = getRollbackTrackedDeltas(globalState);
  const touchedLabel = new Set(
    rollbackDeltas.map((delta) => {
      return delta.segmentLabel;
    }),
  );
  const editMode = !!configStore.get().EditMode;
  const isLumenEditMode = configStore.get().EditMode === EditNames.lumen;
  if (isLumenEditMode) {
    globalState.undoStack.forEach((delta) => {
      syncLumenStrokeDeltaToCprLabelData(delta, true);
    });
    resetManualCprLabelSyncTargetsToBaseline();
  }
  const dirtyIndicesList = editMode
    ? rollbackDeltas.map((delta) => delta.flippedIndices)
    : [];

  const [nx, ny, nz] = globalState.vol.dim;
  const nvox = nx * ny * nz;

  // 바뀐거만할까?
  // 아니 그냥 다지우고 캐심나
  touchedLabel.forEach((label) => {
    let mask = globalState?.masks.get(label)!;
    let { data } = mask;
    data.fill(0);

    const indices = globalState?.labelIndexCache?.get(label)!;
    indices?.forEach((index) => (data[index] = 1));
    globalState?.masks.set(label, {
      data,
      editsAdded: [],
      editsRemoved: [],
    });

    if (globalState?.activeSeg === label) {
      globalState.setCompleteScalarDataArray(data);
      globalState!.updateElements();

      //3d 바꾸면 actorㅅㄲ들이 중복처리됨. 그냥 빼버리기.
      // const baseTiles = tilesOwnedByIndices(indices,nx,ny,nz);
      // const activatedTiles = new Set(tileMap.keys());
      // const tileKeys =  unionTileKeys(baseTiles,new Set<number>(),new Set<number>(),activatedTiles);

      // refreshTiles(cntx,tileKeys,data);
    }
  });

  dirtyIndicesList.forEach((indices) => {
    markDirtyTilesFromIndices(globalState!, indices);
  });
  globalState.undoStack = [];
  globalState.redoStack = [];
  globalState.externalDeltas.clear();
  globalState.externalBaselines.clear();
  updateState();
  if (editMode) {
    requestRenderUpdate(cntx);
  }
}
export function undoSegState(cntx : SurfaceContext) {
  if(!globalState) return;
  const delta = globalState.undoStack.pop();
  if (!delta) return;
  ensureActiveMask(delta.segmentLabel,cntx);
  fillIndices(delta.flippedIndices,Number(!delta.segmentIndex),delta.segmentLabel);
  syncLumenStrokeDeltaToCprLabelData(delta, true);
  notifySegmentHistoryChanged(delta, true);

  requestRenderUpdate(cntx);
  globalState.redoStack.push(delta);
  updateState();
}

export function redoSegState(cntx : SurfaceContext){
  if(!globalState) return;
  const delta = globalState.redoStack.pop();
  if (!delta) return;
  ensureActiveMask(delta.segmentLabel,cntx);
  fillIndices(delta.flippedIndices,Number(delta.segmentIndex),delta.segmentLabel);
  syncLumenStrokeDeltaToCprLabelData(delta);
  notifySegmentHistoryChanged(delta);

  requestRenderUpdate(cntx);
  globalState.undoStack.push(delta);
  updateState();
}

function updateState(){
  if(!globalState) return;
  globalState.stateApplier?.undoableSetter(!!globalState.undoStack.length);
  globalState.stateApplier?.redoableSetter(!!globalState.redoStack.length);
  globalState.stateApplier?.activeLabelSetter(globalState.activeSeg);
}
export function paintAtPointIndices(indices? : number[],threshing?:boolean ){
  if(!globalState || !indices) return;
  if(!indices.length) return [];

  // fill하는데 있어서 이미 masking인것들을 제거해야함.
  // preview는 다 보여주지만 여기서 빼주자.
  if(threshing){
    const segmentMask = ensureActiveMask(globalState.activeSeg!)
    if(!segmentMask) return;
    const {data} = segmentMask;
    const finedIndices = indices.filter(index=>{
      const flag = !(data[index]&0b1);
      if(!flag){//안칠해질거 그냥 원복
        globalState!.setAtIndex(index,1);
      }
      return flag;
    });
    // console.log(finedIndices.length,indices.length);
    fillIndices(new Uint32Array(finedIndices),1);
    return finedIndices;
  }else{
    fillIndices(new Uint32Array(indices),1);
    return indices;
  }
}

export function hasSegmentationState(){return !!globalState;};

export function registerStateApplier(applier:StateApplier){
  if(!globalState) return;
  globalState = {
    ...globalState,
    stateApplier:applier
  };
}

export function subscribeSegmentHistoryChange(listener: SegmentHistoryListener) {
  segmentHistoryListeners.add(listener);
  return () => {
    segmentHistoryListeners.delete(listener);
  };
}

export function clearSegState(context?: SurfaceContext){
  if (context) {
    clearRendererActors(context.renderer);
  } else {
    tileMap.forEach(({mapper}) => {
      mapper.setInputData?.(null);
    });
    tileMap.clear();
  }
  globalState=null;
}
export function initializeSegState(params : InitSegState){
  globalState = createSegState(params);
}
function makeTilePayloads(
  maskFull: Uint8Array,
  volDim: Vec3,
  tiles: TileID[],
  tileSize: number,
  halo: number
): WorkTilePayload[] {
  const [NX,NY,NZ] = volDim;
  const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(max, value));
  const out: WorkTilePayload[] = [];
  // console.time('makeTilePayloads')
  for (const {tx,ty,tz} of tiles) {
    const i0 = clamp(tx*tileSize - halo, 0, NX);
    const j0 = clamp(ty*tileSize - halo, 0, NY);
    const k0 = clamp(tz*tileSize - halo, 0, NZ);
    const i1 = clamp((tx+1)*tileSize + halo, 0, NX);
    const j1 = clamp((ty+1)*tileSize + halo, 0, NY);
    const k1 = clamp((tz+1)*tileSize + halo, 0, NZ);

    const sx = i1 - i0; // 33, 34 ...
    const sy = j1 - j0;
    const sz = k1 - k0;
    if (sx <= 0 || sy <= 0 || sz <= 0) continue;

    const sub = new Uint8Array(sx*sy*sz);
    const lin = (i:number,j:number,k:number) => (k*NY + j)*NX + i;
    const tin = (i:number,j:number,k:number) => (k*sy + j)*sx + i;

    for (let k=k0; k<k1; ++k) {
      for (let j=j0; j<j1; ++j) {
        for (let i=i0; i<i1; ++i) {
          sub[tin(i-i0, j-j0, k-k0)] = maskFull[lin(i,j,k)];
        }
      }
    }

    out.push({ tile:{tx,ty,tz}, i0,j0,k0, sx,sy,sz, mask: sub,halo,tileSize });
  }
  // console.timeEnd('makeTilePayloads')
  return out;
}


type Stage3Params = {
  volInfo: VolumeInfo;
  volDim: Vec3;
  tileSize: number;
  halo: number;
  maskBinary: Uint8Array;
  strategy:'SN'|'MC'|'HP';
  segmentLabel: SegmentationControllerType;
  color?:Vec3;
  // opacity?: number;
  renderer: any;
  requestRender: () => void;
};
type MeshTilesSmoothTiming = {
  mode?: string;
  segmentLabel?: string | null;
  quality?: 'preview' | 'final';
  weightPolicy?: 'static' | 'dynamic';
  boundaryPolicy?: 'fixed';
  iterations?: number;
  finalIterations?: number;
  lambda?: number;
  inputVertexCount?: number;
  inputTriangleCount?: number;
  weldedVertexCount?: number;
  outputTriangleCount?: number;
  smoothMs?: number;
  totalMs?: number;
  baselineSmoothMs?: number;
  baselineIterations?: number;
  staticSampleIterations?: number;
  staticSampleSmoothMs?: number;
  dynamicWeightPolicy?: 'static' | 'dynamic';
  dynamicIterations?: number;
  dynamicSmoothMs?: number;
  dynamicVsStaticMaxDelta?: number;
  dynamicVsStaticMeanDelta?: number;
  dynamicFallbackReason?: string;
  fallback?: boolean;
  fallbackReason?: string;
};

function updateTiles(params: Stage3Params, dirtyKeys: number[]) {
  if (dirtyKeys.length === 0) return;
  const updateGeneration = ++editMeshSmoothingGeneration;
  const tiles = dirtyKeys.map(k=>{
    // const [tx,ty,tz] = k.split(',').map(Number);
    // return { tx,ty,tz };
    return bitwiseKeyInv(k);
  });
  const payloads = makeTilePayloads(
    params.maskBinary,
    params.volDim,
    tiles,
    params.tileSize,
    params.halo
  );

  const color = configStore.get().manualColors['3D'];
  const currentTileColor = color.map((value) =>
    value > 1 ? value / 255 : value,
  ) as Vec3;



  const isInitialBuild = tileMap.size === 0;
  const isCoronarySegment = params.segmentLabel === 'coronary';
  const previewQuality = isCoronarySegment ? 'final' : 'preview';
  let completed = 0;
  let comparisonTimingRequested = false;
  let settled = 0;
  let previewPhaseComplete = false;
  let scheduledFinalRefinements = 0;
  let completedFinalRefinements = 0;
  const smoothingTimings: MeshTilesSmoothTiming[] = [];
  const scheduleIdleTask = (callback: () => void) => {
    const requestIdle = (globalThis as {
      requestIdleCallback?: (
        callback: () => void,
        options?: { timeout: number },
      ) => unknown;
    }).requestIdleCallback;

    if (typeof requestIdle === 'function') {
      requestIdle(callback, { timeout: 250 });
      return;
    }

    setTimeout(callback, 120);
  };
  const logTimingSummary = (phase: 'preview' | 'idle-final') => {
    if (!smoothingTimings.length) return;

    const serverTimings = smoothingTimings.filter(
      (timing) => timing.mode === 'server-cotangent-laplacian',
    );
    const baselineTimings = smoothingTimings.filter(
      (timing) => timing.baselineSmoothMs !== undefined,
    );
    const dynamicTimings = smoothingTimings.filter(
      (timing) => timing.dynamicSmoothMs !== undefined,
    );
    const isFiniteNumber = (value: unknown): value is number =>
      typeof value === 'number' && Number.isFinite(value);
    const sum = (values: number[]) =>
      values.reduce((total, value) => total + value, 0);
    const max = (values: number[]) =>
      values.length ? Math.max(...values) : 0;
    const roundTiming = (value: number) => Math.round(value * 10) / 10;
    const roundRatio = (value: number) => Math.round(value * 1000) / 1000;
    const serverSmoothValues = serverTimings
      .map((timing) => timing.smoothMs)
      .filter(isFiniteNumber);
    const serverTotalValues = serverTimings
      .map((timing) => timing.totalMs)
      .filter(isFiniteNumber);
    const baselineSmoothValues = baselineTimings
      .map((timing) => timing.baselineSmoothMs)
      .filter(isFiniteNumber);
    const dynamicSmoothValues = dynamicTimings
      .map((timing) => timing.dynamicSmoothMs)
      .filter(isFiniteNumber);
    const dynamicMaxDeltaValues = dynamicTimings
      .map((timing) => timing.dynamicVsStaticMaxDelta)
      .filter(isFiniteNumber);
    const dynamicMeanDeltaValues = dynamicTimings
      .map((timing) => timing.dynamicVsStaticMeanDelta)
      .filter(isFiniteNumber);
    const serverSmoothMsTotal = sum(serverSmoothValues);
    const baselineSmoothMsTotal = sum(baselineSmoothValues);
    const serverVertexIterations = sum(
      serverTimings
        .filter((timing) => isFiniteNumber(timing.smoothMs))
        .map((timing) =>
          isFiniteNumber(timing.weldedVertexCount) &&
          isFiniteNumber(timing.iterations)
            ? timing.weldedVertexCount * timing.iterations
            : 0,
        ),
    );
    const baselineVertexIterations = sum(
      baselineTimings
        .filter((timing) => isFiniteNumber(timing.baselineSmoothMs))
        .map((timing) =>
          isFiniteNumber(timing.weldedVertexCount) &&
          isFiniteNumber(timing.baselineIterations)
            ? timing.weldedVertexCount * timing.baselineIterations
            : 0,
        ),
    );
    const baselineProjectedSmoothMsTotal =
      baselineVertexIterations > 0
        ? roundTiming(
            (baselineSmoothMsTotal / baselineVertexIterations) *
              serverVertexIterations,
          )
        : undefined;

  };
  const maybeLogFinalTimingSummary = () => {
    if (
      previewPhaseComplete &&
      scheduledFinalRefinements > 0 &&
      completedFinalRefinements === scheduledFinalRefinements
    ) {
      logTimingSummary('idle-final');
    }
  };
  const done = () => {
    completed += 1;
    if (completed === payloads.length || payloads.length === 0) {
      if (!configStore.get().EditMode) {
        clearRendererActors(params.renderer);
      } else {
        params.renderer.set({
          init: true,
        });
        params.renderer.resetCamera();
      }
    }
  };

  // const steps = Math.floor((payloads.length - 1)*0.2);
  payloads.forEach((payload,index)=>{
    runWorkerTask({type:'meshTiles',payload,strategy:params.strategy})
    .then(async (m: MeshReply) => {
      if(!configStore.get().EditMode) return;
      // const key = `${m.tile.tx},${m.tile.ty},${m.tile.tz}`;
      const key = bitwiseKey(m.tile.tx,m.tile.ty,m.tile.tz);//`${m.tile.tx},${m.tile.ty},${m.tile.tz}`;

      let collectComparisonTiming = false;
      if (!comparisonTimingRequested && m.positions.length > 0) {
        comparisonTimingRequested = true;
        collectComparisonTiming = true;
      }

      const {positions,indices,timing} = await runWorkerTask({
        type:'meshTilesSmooth',
        positions:m.positions,
        indices:m.indices,
        segmentLabel: params.segmentLabel,
        smoothingMode: 'server-cotangent-laplacian',
        smoothingQuality: previewQuality,
        collectComparisonTiming,
        collectDynamicComparisonTiming: collectComparisonTiming,
      });
      if (timing) smoothingTimings.push(timing);

      const entry = createOrGetTileActor(
        tileMap,
        key,
        params.renderer,
        params.color?? currentTileColor,
        // params.opacity ?? 0.6
      );
      // console.log(entry,m.positions.length)

      updateTileActor(entry, positions, indices);
      if (!isCoronarySegment && m.positions.length > 0) {
        scheduledFinalRefinements += 1;
        scheduleIdleTask(async () => {
          try {
            if (
              updateGeneration !== editMeshSmoothingGeneration ||
              !configStore.get().EditMode
            ) {
              return;
            }

            const refined = await runWorkerTask({
              type:'meshTilesSmooth',
              positions:m.positions,
              indices:m.indices,
              segmentLabel: params.segmentLabel,
              smoothingMode: 'server-cotangent-laplacian',
              smoothingQuality: 'final',
              collectComparisonTiming: false,
              collectDynamicComparisonTiming: false,
            });

            if (
              updateGeneration !== editMeshSmoothingGeneration ||
              !configStore.get().EditMode
            ) {
              return;
            }

            if (refined.timing) smoothingTimings.push(refined.timing);
            updateTileActor(entry, refined.positions, refined.indices);
            params.requestRender();
          } catch (err) {
            console.warn(err);
          } finally {
            completedFinalRefinements += 1;
            maybeLogFinalTimingSummary();
          }
        });
      }
      // .then((:{positions:Float32Array,indices:Uint32Array})=>{
      //   updateTileActor(entry, positions, indices);
      // })
      // if(index%steps === 0)
      // params.requestRender();
    })
    .catch(err=>{
      console.warn(err)
    }).finally(()=>{
      settled += 1;
      if (settled === payloads.length) {
        previewPhaseComplete = true;
        logTimingSummary('preview');
        maybeLogFinalTimingSummary();
      }
      if(isInitialBuild){
        done();
      }
      params.requestRender();
    });
  })
}

export function requestRenderUpdate(context: SurfaceContext, onlyLabel : boolean = false) {
  requestAnimationFrame(() => {
    if(!globalState) return;

    globalState.updateElements();

    if(!onlyLabel){
      const dirtyTiles = new Set<number>(globalState.dirtyTiles);
      if (!dirtyTiles.size) return;

      const mask = globalState.masks.get(globalState.activeSeg!);

      if (!mask) return;

      let maskBinary = mask.data;

      if (!!configStore.get().EditMode) {
        if (!globalState.activeSeg) {
          globalState.dirtyTiles.clear();
          return;
        }
        const activeMask = globalState.masks.get(globalState.activeSeg);
        if (!activeMask) {
          globalState.dirtyTiles.clear();
          return;
        }
        maskBinary = activeMask.data;
      }

      const {renderer,rendererId} = context
      updateTiles({
        volInfo: globalState.vol,
        volDim: globalState.vol.dim,
        tileSize: TILE_SIZE,
        halo: TILE_HALO,
        strategy:"HP",
        segmentLabel: globalState.activeSeg!,
        maskBinary,
        renderer,
        requestRender() {
          scheduleRender(rendererId,{type:'updateRenderWindow'})
        },
      }, Array.from(dirtyTiles));

      globalState.dirtyTiles.clear();
    }
  });
}

export function packManipSegState(){
  if(!globalState) return;
  const [dimX, dimY] = globalState.vol.dim;
  const dimXY = dimX * dimY;
  const cachedStats = globalState.cachedStats;
  const {generalSetting} = configStore.get();
  const isLowQuality =
    (cachedStats.lowQuality as boolean | undefined) ??
    generalSetting.renderingQuality !== 'high';

  const indexToXYZ = (i:number) => {
    const z = Math.floor(i / (dimXY));
    const y = Math.floor((i % (dimXY)) / dimX);
    const x = i % dimX;
    return { x, y, z };
  }

  const convert = (value: number) => {
    const bits12 = BigInt(value & 0xfff);
    return bits12 & 0x7ffn;           // 하위 1비트 제거
  };
  const makeBit = (
    x: number,
    y: number,
    z: number,
    isRemoved: 0 | 1,
    component: number
  ): bigint =>
    (convert(x) << 53n) | // 64 - 11
    (convert(y) << 42n) | // 64 - 22
    (convert(z) << 31n) | // 64 - 33
    (BigInt(isRemoved) << 30n) |
    BigInt(component);

  const byIndex = new Map<number, { 0: number; 1: number }>();
  const changedLabelSet = new Set<number>();

  const externalDeltas = Array.from(globalState.externalDeltas.values()).flat();
  const deltas = [...globalState.undoStack, ...externalDeltas];

  for (const delta of deltas) {
    const label = delta.segmentLabel as keyof typeof BitMaskMap;
    const bit = BitMaskMap[label];
    if (bit === undefined) continue;
    const seg = delta.segmentIndex as 0 | 1;
    const other = seg === 1 ? 0 : 1;
    changedLabelSet.add(Math.log2(bit));
    for (const idx of delta.flippedIndices) {
      let entry = byIndex.get(idx);
      if (!entry) {
        entry = { 0: 0, 1: 0 };
        byIndex.set(idx, entry);
      }
      entry[seg]   |= bit;   // 현재 segment에 추가
      entry[other] &= ~bit;  // 반대 segment에서 제거
    }
  }

  const isRegulator = configStore.get().EditMode === EditNames.regulator;
  const interpolatedZ = cachedStats.interpolatedZ as number;
  const originZ = cachedStats.originZ as number;
  const zScale = (originZ  - 1) / (interpolatedZ  - 1);

  const expandToOriginalCoords = (x: number, y: number, z: number) => {
    if (!isLowQuality) {
      return [{ x, y, z }];
    }

    const originalX = x * 2;
    const originalY = y * 2;
    const originalZ = z * 2;
    const coords: { x: number; y: number; z: number }[] = [];

    for (let dz = 0; dz < 2; dz += 1) {
      for (let dy = 0; dy < 2; dy += 1) {
        for (let dx = 0; dx < 2; dx += 1) {
          coords.push({
            x: originalX + dx,
            y: originalY + dy,
            z: originalZ + dz,
          });
        }
      }
    }

    return coords;
  };

  const resultMap = new Map<string, bigint>();
  for (const [idx, entry] of byIndex) {
    const { x, y, z } = indexToXYZ(idx);
    const expandedCoords = expandToOriginalCoords(x, y, z);

    for (const seg of [0, 1] as const) {
      if (entry[seg] === 0) continue;

      for (const coord of expandedCoords) {
        const scaledZ = isRegulator
          ? Math.round(coord.z * zScale)
          : coord.z;
        const key = `${coord.x},${coord.y},${scaledZ},${seg}`;
        const bit = makeBit(coord.x, coord.y, scaledZ, seg, entry[seg]);

        resultMap.set(key, (resultMap.get(key) ?? 0n) | bit);
      }
    }
  }

  //const resultBits = Array.from(resultMap.values(), (v) => v.toString(2));
  //const resultBitInts = Array.from(resultMap.values());
  // const debugLines = [...byIndex.entries()]
  // .map(
  //   ([i, e]) => `${i}:${e[0].toString(2)}/${e[1].toString(2)}`
  // );
  //console.log(resultBits);

  return {resultBitInts: Array.from(resultMap.values()), changedLabels: Array.from(changedLabelSet)};
}

export async function packRegulatorState(selectedId: string) {
  if (!globalState) return;
  const [dimX, dimY] = globalState.vol.dim;
  const cachedStats = globalState.cachedStats;
  const interpolatedZ = cachedStats.interpolatedZ as number;
  const originZ = cachedStats.originZ as number;

  const volume = getManualVolumeFromSeriesId(selectedId);
  if (!volume) return;
  const { imageData, voxelManager } = volume;
  if (!imageData || !voxelManager) {
    console.log(`ImageData or voxelManager not found`);
    return;
  }
  const scalarData = voxelManager.getCompleteScalarDataArray?.();
  if (!scalarData) {
    console.log(`Scalar data not found`);
    return;
  }
  if (!(scalarData instanceof Uint8Array)) {
    console.log(`Scalar data must be Uint8Array`);
    return;
  }
  const workerScalarData = scalarData.slice();
  const { data: resultBitInts } = await runWorkerTask({
      type: 'packRegulatorState',
      src: workerScalarData,
      xSize: dimX,
      ySize: dimY,
      zSize: interpolatedZ,
      targetSize: originZ,
  }, [workerScalarData.buffer]);

  // const counts = new Map<number, number>();
  // for (const v of scalarData) {
  //   counts.set(v, (counts.get(v) ?? 0) + 1);
  // }
  // console.log([...counts.entries()].sort((a, b) => a[0] - b[0]),originZ,interpolatedZ);

  if (!resultBitInts) return;
  return {
    resultBitInts,
    changedLabels: [7],
  };
}


export function applyScreenPolygonEditState(
  indices: Uint32Array,
  cntx: SurfaceContext,
  segmentIndex = 1,
) {
  if (!globalState || !indices.length || !globalState.activeSeg) return null;

  const segmentLabel = globalState.activeSeg;
  ensureActiveMask(segmentLabel, cntx);

  const delta = buildDeltaForTargetValue(indices, segmentLabel, segmentIndex);
  if (!delta) return null;

  applyExternalDelta(delta);

  const segmentMask = globalState.masks.get(segmentLabel);
  if (segmentIndex === 1) {
    segmentMask?.editsAdded.push(delta.flippedIndices);
  } else {
    segmentMask?.editsRemoved.push(delta.flippedIndices);
  }

  globalState.redoStack = [];
  globalState.undoStack.push(delta);
  syncLumenStrokeDeltaToCprLabelData(delta);

  requestRenderUpdate(cntx);
  updateState();

  return {
    voxelCount: delta.flippedIndices.length,
  };
}
